// Global packages and components
import { Link } from '@remix-run/react'
import { createBreadcrumbs, getSchema, Schema } from '~/utils'
import { SlashIcon } from '@heroicons/react/24/solid'
import { HomeIcon } from '@heroicons/react/20/solid'
import { clsx } from 'clsx'

// Types
type Breadcrumb = { name: string; path: string }

interface Props {
  path: string
  removePrevious?: boolean
  insertBeforePage?: Breadcrumb[]
}

// Get previous element
const getPreviousIndex = (array: Breadcrumb[]) => {
  return array.length - 1
}

// Main export
const Breadcrumbs = ({ path, removePrevious, insertBeforePage }: Props) => {
  // Create breadcrumbs from the path
  const allBreadcrumbs = createBreadcrumbs(path).filter(
    e => e.name !== 'C' && e.name !== 'P'
  )

  // Find out the last index
  const lastIndex = getPreviousIndex(allBreadcrumbs)

  // Filter array based on "removePrevious"
  const breadcrumbs = removePrevious
    ? allBreadcrumbs.filter((e, i) => {
        if (i == lastIndex - 1) {
          return false
        } else {
          return true
        }
      })
    : allBreadcrumbs

  // Insert custom item based on "insertBeforePage"
  if (insertBeforePage) {
    breadcrumbs.splice(getPreviousIndex(breadcrumbs), 0, ...insertBeforePage)
  }

  const breadcrumbCrumbs =
    breadcrumbs.length > 1
      ? breadcrumbs.map(b => ({
          name: b.name,
          url: b.path,
        }))
      : []

  const breadcrumbJsonLd =
    breadcrumbCrumbs.length > 0
      ? getSchema({
          type: 'breadcrumbs',
          crumbs: breadcrumbCrumbs,
        })
      : null

  return (
    <>
      {breadcrumbJsonLd && (
        <Schema
          data={breadcrumbJsonLd}
          includeGlobal={false}
        />
      )}
      <div className="container mb-5 mt-5 lg:mb-8">
        <ul className="flex flex-wrap items-center gap-1 lg:gap-2">
          {breadcrumbs.map((e, i) => (
            <li
              key={e.path}
              className={clsx('items-center', {
                'hidden md:flex': i == 0,
                flex: i !== 0,
              })}
            >
              {e.path !== path ? (
                <Link
                  to={e.path}
                  className="flex items-center gap-1 text-2xs font-semibold leading-none text-forest-green underline md:text-xs"
                >
                  {i == 0 && <HomeIcon className="size-3" />}
                  {e.name}
                </Link>
              ) : (
                <span className="text-2xs text-neutral-500 md:text-xs">
                  {e.name}
                </span>
              )}
              {i !== breadcrumbs.length - 1 && (
                <span className="pl-1 lg:pl-2">
                  <SlashIcon className="size-3 text-neutral-500" />
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </>
  )
}

export default Breadcrumbs
